/**
 * Sectors layer (Task #64, operator-cop-doctrine §4.3). Renders the OPEN sectors
 * of the currently-selected incident as operator-drawn polygons with a thin
 * DASHED boundary and no competing fill (§4.3) — the AMBIENT register: never
 * pulses, never obscures the hazard polygons or anomaly dots above it (§3B.8).
 *
 * Incident-scoped (unlike the bbox-driven ambient layers): MapView calls
 * setIncident(incidentId) when a fire/hotspot incident is selected, and the layer
 * fetches + renders that incident's sectors. refreshNow() re-pulls after a
 * draw/edit/close. Each sector is two entities — a faint-fill polygon (the click
 * target) + a dashed polyline boundary — both mapping back to the Sector via
 * pick().
 *
 * Imperative installer (MapView owns the viewer), mirroring StagingAreasLayer.
 */
import * as Cesium from 'cesium';
import { fetchSectors } from '../api';
import type { Sector, SectorListResponse } from '@tsr/shared-types';
import {
  SECTOR_DASH_PATTERN,
  SECTOR_FILL_ALPHA,
  SECTOR_STROKE_ALPHA,
  sectorStyle,
} from '../styles/sectorStyles';

export const SECTOR_PREFIX = 'sector:';
const SECTOR_OUTLINE_PREFIX = 'sector-outline:';

export interface SectorsLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Show closed sectors too (greyed). Default false. */
  includeClosed?: boolean;
  fetcher?: (
    opts: { incident_id: string; include_closed?: boolean },
    signal: AbortSignal,
  ) => Promise<SectorListResponse>;
}

export interface SectorsLayerHandle {
  /** Point the layer at an incident (null clears it). Fetches + renders sectors. */
  setIncident(incidentId: string | null): void;
  /** Re-fetch the current incident's sectors (after a draw / edit / close). */
  refreshNow(): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): Sector | null;
  /** The sectors currently rendered (for callers that need the list). */
  current(): Sector[];
  destroy(): void;
}

/** Average of a polygon's outer-ring vertices — a good-enough label anchor. */
function ringCentroid(ring: Array<[number, number]>): [number, number] {
  let lon = 0;
  let lat = 0;
  // The ring is closed (last == first); skip the repeat so it doesn't double-weight.
  const n = ring.length > 1 ? ring.length - 1 : ring.length;
  for (let i = 0; i < n; i++) {
    lon += ring[i]![0];
    lat += ring[i]![1];
  }
  return [lon / n, lat / n];
}

export function installSectorsLayer(
  viewer: Cesium.Viewer,
  opts: SectorsLayerOptions = {},
): SectorsLayerHandle {
  const fetcher = opts.fetcher ?? fetchSectors;
  const ds = new Cesium.CustomDataSource('sectors');
  void viewer.dataSources.add(ds);

  const entityToSector = new Map<Cesium.Entity, Sector>();
  let rendered: Sector[] = [];
  let incidentId: string | null = null;
  let inflight: AbortController | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (sectors: Sector[]): void => {
    ds.entities.removeAll();
    entityToSector.clear();
    rendered = sectors;
    for (const sector of sectors) {
      const ring = sector.geometry?.coordinates?.[0] as Array<[number, number]> | undefined;
      if (!ring || ring.length < 4) continue;
      const style = sectorStyle(sector.closed_at);
      const strokeColor = Cesium.Color.fromCssColorString(style.color);
      const flat: number[] = [];
      for (const [lon, lat] of ring) flat.push(lon, lat);
      const positions = Cesium.Cartesian3.fromDegreesArray(flat);

      // Faint-fill polygon — the click target. No saturated fill (§4.3). No
      // `height` → it drapes on the globe/terrain as a ground primitive, so it
      // renders correctly over relief AND picks reliably (a height-0 polygon
      // z-fights the surface and is hard to pick).
      const poly = ds.entities.add({
        id: `${SECTOR_PREFIX}${sector.id}`,
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(positions),
          material: strokeColor.withAlpha(SECTOR_FILL_ALPHA),
          // No Cesium outline (1px, non-dashed); the dashed polyline below is the
          // boundary per §4.3.
          outline: false,
        },
      });
      entityToSector.set(poly, sector);

      // Thin dashed boundary — the visible sector edge (ambient register).
      // clampToGround so the dashes drape on terrain instead of cutting through it.
      const outline = ds.entities.add({
        id: `${SECTOR_OUTLINE_PREFIX}${sector.id}`,
        polyline: {
          positions,
          width: 2,
          material: new Cesium.PolylineDashMaterialProperty({
            color: strokeColor.withAlpha(SECTOR_STROKE_ALPHA),
            dashPattern: SECTOR_DASH_PATTERN,
          }),
          clampToGround: true,
        },
      });
      entityToSector.set(outline, sector);

      // Sector name + lead label at the centroid (ambient muted label).
      const [clon, clat] = ringCentroid(ring);
      ds.entities.add({
        id: `sector-label:${sector.id}`,
        position: Cesium.Cartesian3.fromDegrees(clon, clat),
        label: {
          text: sector.lead_label ? `${sector.name}\n${sector.lead_label}` : sector.name,
          font: '11px sans-serif',
          fillColor: strokeColor,
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString('#0b1220').withAlpha(0.6),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(5.0e5, 1.0, 6.0e6, 0.4),
        },
      });
    }
    opts.onCount?.(sectors.length);
  };

  const refreshNow = async (): Promise<void> => {
    if (destroyed) return;
    if (incidentId === null) {
      render([]);
      return;
    }
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    const forIncident = incidentId;
    try {
      const res = await fetcher(
        { incident_id: forIncident, include_closed: opts.includeClosed },
        ctl.signal,
      );
      if (destroyed || ctl.signal.aborted || gen !== loadGen || incidentId !== forIncident) return;
      render(res.sectors);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchSectors failed', err);
    }
  };

  return {
    setIncident(id: string | null): void {
      if (incidentId === id) return;
      incidentId = id;
      void refreshNow();
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): Sector | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToSector.get(entity as Cesium.Entity) ?? null;
    },
    current(): Sector[] {
      return rendered;
    },
    destroy(): void {
      destroyed = true;
      if (inflight) inflight.abort();
      entityToSector.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
